// WindowedDocumentViewer.tsx
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Page, pdfjs, Document } from 'react-pdf';
import { memo, useRef, useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';

import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import { CircularProgress } from '@mui/material';

import { useTranslate } from 'src/locales';


pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs` as string;

interface Props {
  fileUrl: string;
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

interface DocumentDimensions {
  width: string;
  height: string;
  minWidth?: string;
}

function getDocumentDimensions(fileType: string): DocumentDimensions {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxHeight = Math.min(viewportHeight * 0.9, 900); // 90vh or 900px, whichever is smaller

  switch (fileType) {
    case 'pdf':
      return {
        width: '800px',
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    case 'doc':
    case 'docx':
      return {
        width: `${Math.min(viewportWidth * 0.75, 1200)}px`,
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    case 'xls':
    case 'xlsx':
      return {
        width: '90vw',
        height: `${maxHeight}px`,
        minWidth: '1000px'
      };
    case 'ppt':
    case 'pptx':
      return {
        width: `${Math.min(viewportWidth * 0.8, 1200)}px`,
        height: `${maxHeight}px`,
        minWidth: '800px'
      };
    default:
      return {
        width: '800px',
        height: `auto`,
        minWidth: '800px'
      };
  }
}

const PDFViewer = memo(({ url, width, onError }: { url: string; width: number; onError: () => void }) => {
  const { t } = useTranslate();
  const [pageCount, setPageCount] = useState<number | null>(null);
  const passwordAttempts = useRef(0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPageCount(numPages);
  };

  // memoize the file object (prevent re-rendering on nav)
  const file = useMemo(() => ({ url }), [url]);

  const handlePassword = useCallback((callback: Function, reason?: number) => {
    let newPassword = '';

    if (passwordAttempts.current >= 3) {
      alert(t('documents.document_viewer.password_error'));
      onError();
      return;
    }

    passwordAttempts.current += 1;

    switch (reason) {
      case 1: {
        newPassword = prompt(t('documents.document_viewer.password_prompt')) || '';

        callback(newPassword);
        break;
      }
      default:
        newPassword = prompt(t('documents.document_viewer.incorrect_password_prompt')) || '';
        callback(newPassword);
        break;
    }
  }, [passwordAttempts, t, onError]);

  return (
    <Document
      className="document-viewer-document"
      onPassword={handlePassword}
      loading={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      }
      error={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          {t('documents.document_viewer.load_error')}
        </Box>
      }
      file={file}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onError}
    >
      <Stack direction="column" alignItems="center" justifyContent="center">
        {Array.from(new Array(pageCount || 0), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            scale={1}
            width={width}
            loading={
              <Box sx={{
                height: 800,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <CircularProgress size={24} />
              </Box>
            }
          />
        ))}
      </Stack>
    </Document>
  );
});

const OfficeViewer = memo(({ url, dimensions }: { url: string; dimensions: DocumentDimensions }) => {
  const encodedUrl = encodeURIComponent(url);
  const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;

  return (
    <Box sx={{ height: '100%', overflowY: 'hidden' }}>
      <iframe
        src={viewerUrl}
        style={{
          border: 'none',
          width: '100%',
          height: '100%'
        }}
        title="document-viewer"
      />
    </Box>
  );
});

export default function DocumentViewer({ fileUrl }: Props) {
  const documentRef = useRef<HTMLDivElement>(null);

  const [viewerError, setViewerError] = useState(false);

  // Fit the PDF page to the container width (capped at the 800px design width) so it never overflows a
  // narrow surface. The record-panel quick-view drawer (~640px) was clipping a fixed 800px page on the
  // left with no horizontal scroll (JT1-1537 QA). Re-measures on container/viewport resize.
  const [pdfWidth, setPdfWidth] = useState<number>(800);
  useLayoutEffect(() => {
    const el = documentRef.current;
    if (!el) return undefined;
    const measure = () => {
      const available = el.clientWidth - 32; // minus the container padding (p:2 => 16px each side)
      setPdfWidth(Math.max(Math.min(available, 800), 240));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fileType = useMemo(() =>
    getFileExtension(fileUrl || ''),
    [fileUrl]
  );

  const dimensions = useMemo(() =>
    getDocumentDimensions(fileType),
    [fileType]
  );

  useEffect(() => {
    const scrollKey = `doc-scroll-${fileUrl}`;
    return () => {
      if (documentRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        sessionStorage.setItem(scrollKey, documentRef.current.scrollTop.toString());
      }
    };
  }, [fileUrl]);

  useEffect(() => {
    const scrollKey = `doc-scroll-${fileUrl}`;
    const savedScrollPosition = sessionStorage.getItem(scrollKey);
    if (savedScrollPosition && documentRef.current) {
      documentRef.current.scrollTop = parseInt(savedScrollPosition, 10);
    }
  }, [fileUrl]);

  const documentViewer = useMemo(() => {
    if (!fileUrl) return null;

    if (viewerError) {
      return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
    }

    switch (fileType) {
      case 'pdf':
        return <PDFViewer url={fileUrl} width={pdfWidth} onError={() => setViewerError(true)} />;
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
      case 'ppt':
      case 'pptx':
        return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tiff':
      case 'svg':
        return <img src={fileUrl} alt="document-preview" style={{ maxWidth: '100%', height: 'auto' }} />;
      default:
        return <OfficeViewer url={fileUrl} dimensions={dimensions} />;
    }
  }, [fileUrl, viewerError, dimensions, fileType, pdfWidth]);

  return (
    <Box
      ref={documentRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: 2,
        '& .react-pdf__Document': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }
      }}
    >
      {documentViewer}
    </Box>
  );
}